import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import usePrevious from 'use-previous';
import cn from 'classnames';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { PaginationLabel } from '@bibliocommons/pagination';
import { catalogBibShape, childrenShape, paginationShape } from '@bibliocommons/bc-prop-types';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import EmptyUGCDisplay from 'app/components/bibs/shared/ugc/EmptyUGCDisplay';
import CommunityDisclaimer from 'app/components/bibs/shared/ugc/CommunityDisclaimer';

import './UgcList.scss';

export default function UgcList({
  items,
  catalogBib,
  contentType,
  pagination,
  transactions,
  itemClassName,
  renderEditor,
  renderItem,
  children,
  ugcType,
  showDisclaimer
}) {
  const metadataId = catalogBib.get('metadataId');
  const transaction = transactions.get(metadataId);
  const prevItems = usePrevious(items);
  const recentTransaction = useRef(null);
  const editorRef = useRef(null);
  const displayRefs = useRef({});
  const editor = renderEditor?.();
  const currentLibrary = useSelector(selectCurrentLibrary);

  useEffect(() => {
    // Keep track of the most recent user transaction.
    if (transaction) recentTransaction.current = transaction;

    // We assume that any change in items after a recent transaction is becuase of that transaction
    if (recentTransaction.current && !items.equals(prevItems)) {
      recentTransaction.current = null;

      // Determine if an item has been added after the recent transaction and then focus on it
      const addedItem = items.find(item => !prevItems.includes(item));
      if (addedItem && displayRefs.current[addedItem.get('id')]) {
        setFocusWithinElement(displayRefs.current[addedItem.get('id')]);
      }

      // Determine if an item has been removed after the recent transaction and then focus on the "add" button
      const removedItem = prevItems.find(item => !items.includes(item));
      if (!addedItem && removedItem && editorRef.current) {
        setFocusWithinElement(editorRef.current);
      }
    }
  }, [transaction, recentTransaction, items, prevItems, editorRef, displayRefs]);

  function renderItems() {
    return items.size ? (
      items.map(item => {
        if (!item) return null;
        return (
          <div
            key={item.get('id')}
            ref={el => (displayRefs.current[item.get('id')] = el)}
            className={cn('ugc-list-item', itemClassName)}
          >
            {renderItem(item)}
          </div>
        );
      })
    ) : (
      <EmptyUGCDisplay contentType={contentType} />
    );
  }

  function renderUgcDisclaimer() {
    const type = ugcType ?? contentType;
    return (
      currentLibrary?.get('ugcDisclaimerMessageEnabled') &&
      showDisclaimer && <CommunityDisclaimer type={type} currentLibrary={currentLibrary} />
    );
  }

  return (
    <div className="cp-ugc-list">
      {renderUgcDisclaimer()}
      {editor && <div ref={editorRef}>{editor}</div>}
      {pagination?.get('count') > 0 && <PaginationLabel pagination={pagination} autoFocus />}
      {children}
      {renderItems()}
    </div>
  );
}

UgcList.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  contentType: PropTypes.string.isRequired,
  items: ImmutablePropTypes.list.isRequired,
  renderItem: PropTypes.func.isRequired,
  transactions: ImmutablePropTypes.map.isRequired,
  pagination: paginationShape,
  renderEditor: PropTypes.func,
  itemClassName: PropTypes.string,
  children: childrenShape,
  ugcType: PropTypes.string,
  showDisclaimer: PropTypes.bool
};

UgcList.defaultProps = {
  showDisclaimer: false
};
